import { useCallback, useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ReactComponent as SvgSendEmail } from '../../images/svg/icon-email_send.svg'
import { ReactComponent as SvgSuccess } from '../../images/svg/icon-success.svg'
import ImgLogo from '../../images/logo-3_512x512.png'
import ImgWraning from '../../images/warning.png'
import CopyText from '../../components/copy-text'

import Loading from './components/loading'
import { requestVerifyEmail, requestCheckTenantStatus, requestRetryCheckTenant } from './model'
import styles from './styles.module.css'

const Confirm = () => {
  const [searchParams] = useSearchParams()
  const [verified, setVerified] = useState(false)
  const [verifyFailed, setVerifyFailed] = useState()
  const [regtoken, setRegtoken] = useState()
  const [workspaceURL, setWorkspaceURL] = useState()
  const [tenantUrl, setTenantUrl] = useState()
  const [tenantWarning, setTenantWarning] = useState()
  const [retrying, setRetrying] = useState(false)
  const retryCount = useRef(0)
  const [loopError, setLoopError] = useState()

  const handleVerifyEmail = useCallback(() => {
    const token = searchParams.get('token')

    setVerified(false)
    setVerifyFailed(null)
    if (typeof token === 'string' && token.length > 0) {
      requestVerifyEmail(token)
        .then(response => {
          setVerified(true)
          setRegtoken(response.reg_token)
          setWorkspaceURL(response.workspace_url)
          // 计算出租户域名
          const subdoamin = (new URL(response.workspace_url)).host
          // 拼接出id
          const id = `${response.username}@${subdoamin}`
        })
        .catch(e => {
          setVerified(true)
          setVerifyFailed(e.message)
        })
    } else {
      setVerified(true)
      setVerifyFailed('Token lost.')
    }
  }, [searchParams])

  const checkTenantStatusFailed = useCallback(() => {
    setRetrying(false)
    if (retryCount.current > 3) {
      setTenantWarning('We encountered an error while creating your workspace. Please seek assistance from our support team.')
    } else {
      setTenantWarning('We encountered an error while creating your workspace, Please try again.')
    }
  }, [])

  const checkTenantStatus = useCallback((regtoken) => {
    const request = () => {
      const loop = () => {
        setTimeout(() => {
          request()
        }, 1000)
      }

      requestCheckTenantStatus(regtoken)
        .then(response => {
          if (response.status === 99) {
            console.error('Check Tenant Status: 99')
            checkTenantStatusFailed()
          } else if (response.status === 3) {
            setTenantUrl(response.tenant_active_url)
          } else if (response.status === 4) {
            console.error(response.message)
            setLoopError(response.message)
          } else {
            loop()
          }
        })
        .catch((e) => {
          console.error(e.message)
          checkTenantStatusFailed()
        })
    }

    request()
  }, [checkTenantStatusFailed])

  const retryCheckTenant = useCallback(() => {
    retryCount.current += 1
    setRetrying(true)
    requestRetryCheckTenant(regtoken)
      .then(() => {
        checkTenantStatus(regtoken)
      })
      .catch(e => {
        console.error(e.message)
        setTimeout(() => {
          setRetrying(false)
        }, 1000)
      })
  }, [regtoken, checkTenantStatus])

  useEffect(() => {
    const regtoken = searchParams.get('regtoken')
    const workspace = searchParams.get('workspace_url')

    if (typeof regtoken === 'string' && regtoken.length > 0) {
      setVerified(true)
      setRegtoken(regtoken)
      setWorkspaceURL(workspace)
    } else {
      handleVerifyEmail()
    }
  }, [handleVerifyEmail, searchParams])

  useEffect(() => {
    if (typeof regtoken === 'string' && regtoken.length > 0) {
      checkTenantStatus(regtoken)
    }
  }, [checkTenantStatus, regtoken])

  // 验证 email 中
  if (verified !== true) {
    return (
      <div className={styles.loading}>
        <div className={styles.loading_icon}>
          <Loading />
        </div>
        <div className={styles.loading_text}>Verifying...</div>
      </div>
    )
  }

  // email 验证失败
  if (verifyFailed) {
    return (
      <div className={styles.verify_failed_wrapper}>
        <div className={styles.verify_failed}>
          <h1>Verification Failed:</h1>
          <p className={styles.error}>{verifyFailed}</p>
          <button className={styles.button} onClick={handleVerifyEmail}>Try Again</button>
        </div>
      </div>
    )
  }

  // ready to go 状态
  if (tenantUrl) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.email_logo}>
          <SvgSuccess />
        </div>
        <h1 className={styles.title}>Your workspace is ready to go!</h1>
        
        <div className={styles.content_workspace_url}>
          <p>
            <CopyText text={workspaceURL}>
              <a className="word-break" href={workspaceURL} target="_blank" rel="noreferrer">{workspaceURL}</a>
            </CopyText>
          </p>
        </div>
        
        <div className={styles.progress}>
          <div className={styles.progress_bar_full}></div>
        </div>
        
        <div className={styles.content}>
          <a className={styles.link_button} href={tenantUrl}>Get Going</a>
        </div>

        <div className={styles.copyright}>
          <p>{`© ${new Date().getFullYear()} Splashtop`}</p>
        </div>
      </div>
    )
  }

  // retry 中的状态
  if (retrying) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.email_logo}>
          <img src={ImgLogo} alt="logo" style={{ width: '120px', height: '120px' }} />
        </div>
        <h1 className={styles.title}>Creating your workspace...</h1>
        
        <div className={styles.content_workspace_url}>
          <p>
            <CopyText text={workspaceURL}>
              <span className="word-break">{workspaceURL}</span>
            </CopyText>
          </p>
        </div>
        
        <div className={styles.progress}>
          <div className={styles.progress_bar}></div>
        </div>
        
        <div className={styles.content}>
          <p>Your new workspace will be ready shortly. You can choose to wait within this browser tab or close it. As soon as your workspace is ready, we will send you an email notification.</p>
        </div>

        <div className={styles.copyright}>
          <p>{`© ${new Date().getFullYear()} Splashtop`}</p>
        </div>
      </div>
    )
  }

  // 创建 tenant 异常，进入 try again 状态
  if (tenantWarning) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.email_logo}>
          <img src={ImgWraning} alt="logo" style={{ width: '120px' }} />
        </div>
        <h1 className={styles.title}>Creating your workspace...</h1>
        
        <div className={styles.content_workspace_url}>
          <p>
            <CopyText text={workspaceURL}>
              <span className="word-break">{workspaceURL}</span>
            </CopyText>
          </p>
        </div>
        
        <div className={styles.content}>
          <p className={styles.error}>{tenantWarning}</p>
        </div>
        
        <div className={styles.content}>
          <button className={styles.link_button} onClick={retryCheckTenant}>Try Again</button>
        </div>

        <div className={styles.copyright}>
          <p>{`© ${new Date().getFullYear()} Splashtop`}</p>
        </div>
      </div>
    )
  }

  // 创建 tenant 中
  return (
    <div className={styles.wrapper}>
      <div className={styles.email_logo}>
        <SvgSendEmail />
      </div>
      <h1 className={styles.title}>Email confirmed!<br />Creating your workspace... </h1>

      {loopError ? (
        <div className={styles.content} style={{ margin: '62px 0' }}>
          <p className={styles.error}>{loopError}</p>
        </div>
      ) : (
        <div className={styles.progress}>
          <div className={styles.progress_bar}></div>
        </div>
      )}
      
      <div className={styles.content}>
        <p>Your new workspace will be ready shortly. You can choose to wait within this browser tab or close it. As soon as your account is ready, we will send you an email notification.</p>
        <p className={styles.workspace_url}>
          <CopyText text={workspaceURL}>
            <b>Your new workspace is at <span className="word-break">{workspaceURL}</span></b>
          </CopyText>
        </p>
      </div>

      <div className={styles.copyright}>
        <p>{`© ${new Date().getFullYear()} Splashtop`}</p>
      </div>
    </div>
  )
}

export default Confirm
